import type { ReactNode } from "react";
import { formatGpuHours, formatUsd } from "@/components/gpu/usage";
import { Sparkline } from "@/components/metric";
import { formatGpuLimit } from "./gpuQuotaFormat";
import styles from "./GpuQuotaPage.module.css";

// 쿼터 표의 셀. 사람 행(c6 컬럼 render)과 그 아래 workspace 하위 행이 같은 셀을 쓴다. 그래서 두
// 행의 같은 칸은 같은 모양이고, 세로로 바로 비교할 수 있다.

// 셀이 읽는 값. 사람 행·꼬리 행·workspace 하위 행이 모두 이 필드를 가진다. 그래서 행 모델을
// 그대로 전달한다.
export type GpuQuotaCellValues = {
  displayName: string;
  gpuLimit: number | null;
  effectiveGpuLimit: number | null;
  activeGpus: number;
  pendingGpus: number;
  gpuHours: number;
  cost: number | null;
  sessionCount: number;
  dailyGpuHours: readonly number[];
};

// 지금 점유와 지금 적용 중인 한도. 한도가 유한하면 meter 로 비율을 함께 보여 준다. 한도에 닿은
// 행은 채움 색이 바뀐다. 무제한(null)은 비율이 없으므로 meter 를 그리지 않는다. 0 은 유한한
// 한도다. 꼬리 행은 한도 행이 없다(`hasQuota=false`). 그래서 "무제한" 이 아니라 값 없음이다.
export function OccupancyCell({
  values,
  hasQuota,
}: {
  values: GpuQuotaCellValues;
  hasQuota: boolean;
}): ReactNode {
  if (!hasQuota) {
    return <span className={styles.numericIdle}>—</span>;
  }
  const limit = values.effectiveGpuLimit ?? values.gpuLimit;
  const limitText = formatGpuLimit(values.effectiveGpuLimit, values.gpuLimit);
  return (
    <span className={styles.occupancy}>
      {limit === null ? null : <OccupancyMeter active={values.activeGpus} limit={limit} />}
      <span className={styles.numeric}>
        <span className={values.activeGpus > 0 ? styles.numericActive : styles.numericIdle}>
          {values.activeGpus}
        </span>
        {` / ${limitText}`}
      </span>
    </span>
  );
}

function OccupancyMeter({ active, limit }: { active: number; limit: number }): ReactNode {
  // 한도 0 은 "쓸 수 없음" 이다. 그때 점유가 있으면 이미 한도를 넘은 것이다.
  const ratio = limit > 0 ? active / limit : active > 0 ? 1 : 0;
  const percent = Math.round(Math.min(1, Math.max(0, ratio)) * 100);
  const atLimit = active > 0 && active >= limit;
  return (
    <span
      className={styles.meterTrack}
      role="progressbar"
      aria-label={`한도 대비 점유 ${percent}%`}
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <span
        className={atLimit ? `${styles.meterFill} ${styles.meterFillAtLimit}` : styles.meterFill}
        style={{ width: `${percent}%` }}
      />
    </span>
  );
}

// 기간 GPU-hours. 숫자 옆의 막대는 표에서 가장 많이 쓴 사람 행 대비 비율이다. 막대는 크기를
// 훑어보는 용도이고, 값은 숫자가 말한다.
export function GpuHoursCell({
  gpuHours,
  maxGpuHours,
}: {
  gpuHours: number;
  maxGpuHours: number;
}): ReactNode {
  const percent = maxGpuHours > 0 ? Math.min(100, (gpuHours / maxGpuHours) * 100) : 0;
  return (
    <span className={styles.usage}>
      <span className={gpuHours > 0 ? styles.numeric : `${styles.numeric} ${styles.numericIdle}`}>
        {formatGpuHours(gpuHours)}
      </span>
      <span className={styles.usageTrack} aria-hidden="true">
        <span className={styles.usageFill} style={{ width: `${percent}%` }} />
      </span>
    </span>
  );
}

// 기간의 UTC 일별 GPU-hours 모양. 높이는 이 행의 최댓값 기준이다. 그래서 행끼리는 모양만 비교한다.
export function DailyTrendCell({
  dailyGpuHours,
  targetName,
}: {
  dailyGpuHours: readonly number[];
  // 접근성 이름에 넣는 대상 이름. 예) "Seungwan Seo"
  targetName: string;
}): ReactNode {
  const peak = dailyGpuHours.reduce((highest, value) => Math.max(highest, value), 0);
  return (
    <span className={styles.trend}>
      <Sparkline
        values={dailyGpuHours}
        ariaLabel={`${targetName} 일별 GPU-hours, 하루 최대 ${formatGpuHours(peak)}`}
      />
    </span>
  );
}

export function CostCell({ cost }: { cost: number | null }): ReactNode {
  const idle = cost === null || cost === 0;
  return (
    <span className={idle ? `${styles.numeric} ${styles.numericIdle}` : styles.numeric}>
      {formatUsd(cost)}
    </span>
  );
}

// 0 은 muted 로 약하게, 1 이상은 굵게 표시한다. 표를 훑을 때 값이 있는 행이 먼저 보이게 한다.
export function CountCell({ count }: { count: number }): ReactNode {
  const className = [styles.numeric, count > 0 ? styles.numericActive : styles.numericIdle].join(
    " ",
  );
  return <span className={className}>{count}</span>;
}
